import React from 'react';

/**
 * Supplier Gateway UI kit: shared design tokens and primitives for the
 * supplier-facing screens (ASN, Promised Date, Lead Time).
 *
 * `C` = colors, `F` = font stacks, `T` = type scale. `Pill`, `Btn`, `Card`,
 * and `CardHead` are the shared building blocks used across those screens so
 * their look stays consistent.
 */

// Color tokens.
export const C = {
    bg: '#F2F4F7',
    surface: '#FFFFFF',
    border: '#DDE1E6',
    borderLight: '#EBEDF0',
    text: '#111827',
    textSub: '#4B5563',
    textMuted: '#9CA3AF',
    primary: '#0C6ECA',
    primaryHover: '#0A5BA8',
    primarySoft: '#EBF4FF',
    green: '#15803D',
    greenSoft: '#DCFCE7',
    amber: '#B45309',
    amberSoft: '#FEF3C7',
    red: '#B91C1C',
    redSoft: '#FEE2E2',
    blue: '#1D4ED8',
    blueSoft: '#DBEAFE',
    gray: '#6B7280',
    graySoft: '#F3F4F6',
    purple: '#6D28D9',
    purpleSoft: '#EDE9FE',
    nav: '#131C2E',
    navText: '#94A3B8',
    navActive: '#3B82F6',
    rail: '#F8F9FB',
    railBorder: '#E5E7EB',
};

// Font stacks.
export const F = {
    ui: "'Inter','Amazon Ember','Segoe UI',system-ui,sans-serif",
    mono: "'JetBrains Mono','Fira Code','Consolas',monospace",
};

// Type scale.
export const T = {
    caption: 12,
    body: 14,
    bodyLg: 15,
    section: 18,
    title: 24,
    display: 30,
};

type PillColor = 'green' | 'amber' | 'red' | 'blue' | 'gray' | 'purple';

interface PillProps {
    label: string;
    color?: PillColor;
    dot?: boolean;
}

export function Pill({ label, color = 'gray', dot = false }: PillProps) {
    const map: Record<string, [string, string, string]> = {
        green: [C.greenSoft, C.green, '#BBF7D0'],
        amber: [C.amberSoft, C.amber, '#FDE68A'],
        red: [C.redSoft, C.red, '#FECACA'],
        blue: [C.blueSoft, C.blue, '#BFDBFE'],
        gray: [C.graySoft, C.gray, '#D1D5DB'],
        purple: [C.purpleSoft, C.purple, '#DDD6FE'],
    };
    const [bg, fg, border] = map[color] ?? map.gray;
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: bg,
                color: fg,
                border: `1px solid ${border}`,
                borderRadius: 4,
                padding: '2px 9px',
                fontSize: T.caption,
                fontWeight: 600,
                fontFamily: F.ui,
                whiteSpace: 'nowrap',
            }}
        >
            {dot && (
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: fg }} />
            )}
            {label}
        </span>
    );
}

Pill.defaultProps = {
    color: 'gray',
    dot: false,
};

type BtnVariant = 'pri' | 'sec' | 'ghost' | 'danger';
type BtnSize = 'sm' | 'xs' | 'md';

interface BtnProps {
    label: string;
    variant?: BtnVariant;
    size?: BtnSize;
    onClick?: () => void;
    full?: boolean;
    disabled?: boolean;
    title?: string;
}

const BTN_VARIANT_STYLE: Record<BtnVariant, React.CSSProperties> = {
    pri: { background: C.primary, color: '#fff', border: `1px solid ${C.primary}` },
    sec: { background: C.surface, color: C.text, border: `1px solid ${C.border}` },
    ghost: { background: 'transparent', color: C.primary, border: '1px solid transparent' },
    danger: { background: C.redSoft, color: C.red, border: `1px solid #FECACA` },
};

const BTN_PADDING: Record<BtnSize, string> = {
    xs: '5px 11px',
    sm: '7px 15px',
    md: '10px 20px',
};

const BTN_FONT_SIZE: Record<BtnSize, number> = {
    xs: T.caption,
    sm: T.body,
    md: T.section,
};

export function Btn({
    label,
    variant = 'sec',
    size = 'sm',
    onClick,
    full = false,
    disabled = false,
    title,
}: BtnProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            style={{
                ...BTN_VARIANT_STYLE[variant],
                borderRadius: 5,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.7 : 1,
                fontFamily: F.ui,
                padding: BTN_PADDING[size],
                fontSize: BTN_FONT_SIZE[size],
                fontWeight: 500,
                width: full ? '100%' : undefined,
            }}
        >
            {label}
        </button>
    );
}

Btn.defaultProps = {
    variant: 'sec',
    size: 'sm',
    onClick: undefined,
    full: false,
    disabled: false,
    title: undefined,
};

interface CardProps {
    children: React.ReactNode;
    style?: React.CSSProperties;
}

export function Card({ children, style }: CardProps) {
    return (
        <div
            style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                overflow: 'hidden',
                ...style,
            }}
        >
            {children}
        </div>
    );
}

Card.defaultProps = {
    style: undefined,
};

interface CardHeadProps {
    title: string;
    right?: React.ReactNode;
}

export function CardHead({ title, right }: CardHeadProps) {
    return (
        <div
            style={{
                padding: '11px 16px',
                borderBottom: `1px solid ${C.borderLight}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <span style={{ fontSize: T.body, fontWeight: 600, color: C.text, fontFamily: F.ui }}>
                {title}
            </span>
            {right}
        </div>
    );
}

CardHead.defaultProps = {
    right: undefined,
};

// ── Internal-area layout (left sidebar + page title) ──────────────────────────
// Used by the Supplier Control and Supplier Insights areas to match the
// prototype's sidebar-based navigation. Client-side: nav items call `onNav`.

export interface ShellNavItem {
    id: string;
    label: string;
}

export function Shell({
    navItems,
    active,
    onNav,
    accent = C.primary,
    children,
}: {
    navItems: ShellNavItem[];
    active: string;
    onNav: (id: string) => void;
    accent?: string;
    children: React.ReactNode;
}) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, fontFamily: F.ui }}>
            <div
                style={{
                    width: 210,
                    flexShrink: 0,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: '8px 0',
                    marginRight: 16,
                }}
            >
                {navItems.map((n) => {
                    const on = n.id === active;
                    return (
                        <button
                            key={n.id}
                            type="button"
                            onClick={() => onNav(n.id)}
                            style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '10px 18px',
                                fontSize: T.body,
                                fontFamily: F.ui,
                                cursor: 'pointer',
                                background: on ? C.primarySoft : 'transparent',
                                borderLeft: on ? `3px solid ${accent}` : '3px solid transparent',
                                borderTop: 'none',
                                borderRight: 'none',
                                borderBottom: 'none',
                                color: on ? accent : C.textSub,
                                fontWeight: on ? 700 : 500,
                            }}
                        >
                            {n.label}
                        </button>
                    );
                })}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
    );
}

Shell.defaultProps = {
    accent: C.primary,
};

export function PageTitle({
    title,
    subtitle,
    right,
}: {
    title: string;
    subtitle?: string;
    right?: React.ReactNode;
}) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
                gap: 12,
                flexWrap: 'wrap',
            }}
        >
            <div>
                <h1 style={{ margin: 0, fontSize: T.title, fontWeight: 700, color: C.text }}>
                    {title}
                </h1>
                {subtitle ? (
                    <p style={{ margin: '3px 0 0', fontSize: T.body, color: C.textSub }}>{subtitle}</p>
                ) : null}
            </div>
            {right}
        </div>
    );
}

PageTitle.defaultProps = {
    subtitle: undefined,
    right: undefined,
};

export function Field({
    label,
    value,
    mono,
}: {
    label: string;
    value: React.ReactNode;
    mono?: boolean;
}) {
    return (
        <div>
            <div style={{ fontSize: T.caption, color: C.textMuted, marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: T.body, fontFamily: mono ? F.mono : F.ui, color: C.text }}>
                {value}
            </div>
        </div>
    );
}

Field.defaultProps = {
    mono: false,
};
